import { Switch, Button, Typography, Tooltip } from 'antd';
import Label from 'atoms/label/label';
import Loader from 'atoms/loader';
import ModalActions from 'atoms/modal/modalActions';
import ModalBody from 'atoms/modal/modalBody';
import ModalHeader from 'atoms/modal/modalHeader';
import Modal from 'atoms/modal/reactModal';
import { isLoadingEnabled } from 'models/loaders/selectors';
import {
	getUserEmail,
	getUserGrpId,
	getUserTenentList,
	getWebAppSettings,
	isAccessControlEnabled,
	isNewUploadEnabled,
} from 'models/user/selector';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as sagaActions from './sagaActions';
import DropdownInner, { OptionSelection, OptionType } from 'atoms/dropdown/dropdown';
import { updateTenent } from 'models/user/sagaActions';
import Heading from 'atoms/heading/heading';

const { Text, Title } = Typography;

interface SidebarProps {
	hideModal: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ hideModal }) => {
	const dispatch = useDispatch();

	const isLoading = useSelector((state: any) => isLoadingEnabled(state, 'USER_SETTINGS'));
	const isTenentListLoading = useSelector((state: any) => isLoadingEnabled(state, 'tenant-list-loading'));
	const { ReleaseVersion } = useSelector(getWebAppSettings);
	const isAccessControlValue = useSelector(isAccessControlEnabled);
	const isNewUploadValue = useSelector(isNewUploadEnabled);

	const [accessControl, setAccessControl] = useState<boolean>(Boolean(isAccessControlValue));
	const [newUpload, setNewUpload] = useState<boolean>(Boolean(isNewUploadValue));
	const [darkMode, setDarkMode] = useState<boolean>(() => {
		return document.documentElement.getAttribute('data-theme') === 'dark';
	});

	const email = useSelector((state: any) => getUserEmail(state));
	const grpId = useSelector((state: any) => getUserGrpId(state));
	const tenetList = useSelector((state: any) => getUserTenentList(state));

	const userTenentList: OptionType[] = useMemo(
		() =>
			(tenetList ?? []).map((res: any) => ({
				displayName: res.UserName,
				value: res.PartnerID,
			})),
		[tenetList]
	);

	const [partnerId, setPartnerId] = useState<string | null>(grpId);

	const selectedTenent = useMemo(() => {
		const findTenent = userTenentList.find((tenant) => tenant.value === partnerId);
		return findTenent
			? {
					value: findTenent.value,
					displayName: findTenent.displayName,
				}
			: null;
	}, [partnerId, userTenentList]);

	const handleChange = (selectedPartner: OptionSelection) => {
		const newGrp = selectedPartner as OptionType;
		const newGrpId = newGrp ? newGrp.value : null;
		setPartnerId(newGrpId);
	};

	const toggleAccessControl = (isEnabled: boolean) => {
		setAccessControl(isEnabled);
	};

	const toggleDarkMode = (isEnabled: boolean) => {
		setDarkMode(isEnabled);
		document.documentElement.setAttribute('data-theme', isEnabled ? 'dark' : 'light');
		try {
			localStorage.setItem('theta-dark-mode', isEnabled ? '1' : '0');
		} catch (e) {}
	};

	useEffect(() => {
		setAccessControl(Boolean(isAccessControlValue));
	}, [isAccessControlValue]);

	useEffect(() => {
		setNewUpload(Boolean(isNewUploadValue));
	}, [isNewUploadValue]);

	const handleSaveClick = () => {
		dispatch(
			sagaActions.updateUserAgGridSettings({
				IsAccessControlEnabled: accessControl,
				IsNewUploadEnabled: newUpload,
			})
		);
		if (grpId !== partnerId) {
			dispatch(updateTenent({ email, partnerId }));
		}
	};

	return (
		<Modal hideModal={hideModal} height={600} width={520}>
			<ModalHeader>
				<Stack>
					<StackItem isGrow>
						<Heading>User Profile Settings</Heading>
					</StackItem>
					<StackItem>
						<StackItem isGrow isStretch align="right">
							<Label size="tiny">
								Version: {typeof ReleaseVersion === 'string' ? ReleaseVersion.split('-')?.[1] : ''}
							</Label>
						</StackItem>
					</StackItem>
				</Stack>
			</ModalHeader>
			{isLoading ? (
				<StackItem isGrow isStretch>
					<Loader isInset />
				</StackItem>
			) : (
				<>
					<ModalBody>
						<Stack isVertical>
							<StackItem isGrow isStretch>
								<Stack isVertical gutter="medium">
									{/* Access Control */}
									<StackItem>
										<div className="profile-block">
											<StackItem isGrow>
												<Stack isVertical>
													<Title level={5}>Enable Access Control Settings</Title>
													<Text>
														Updates the Application Tables to Support advanced <br />
														Access Controls settings in App Settings.
													</Text>
												</Stack>
											</StackItem>
											<StackItem>
												<Switch checked={accessControl} onChange={toggleAccessControl} />
											</StackItem>
										</div>
									</StackItem>

									{/* New Grid Upload */}
									<StackItem>
										<div className="profile-block">
											<StackItem isGrow>
												<Stack isVertical>
													<Title level={5}>Enable New Grid Upload</Title>
													<Text>Enables new upload flow on grids</Text>
												</Stack>
											</StackItem>
											<StackItem>
												<Switch checked={newUpload} onChange={setNewUpload} />
											</StackItem>
										</div>
									</StackItem>

									{/* Dark Mode */}
									{/* <StackItem>
										<div className="profile-block">
											<StackItem isGrow>
												<Stack isVertical>
													<Title level={5}>Dark Mode</Title>
													<Text>Switch the interface to a dark color scheme</Text>
												</Stack>
											</StackItem>
											<StackItem>
												<Switch checked={darkMode} onChange={toggleDarkMode} />
											</StackItem>
										</div>
									</StackItem> */}

									{/* Tenant Switch */}
									<StackItem className="p-0 m-0">
										<Stack isVertical>
											<StackItem>
												<Title level={5}>Switch to a different Tenant</Title>
											</StackItem>
											<StackItem className="p-0 m-0">
												<DropdownInner
													disabled={!tenetList?.length}
													isLoading={isTenentListLoading}
													key="options"
													selectedOption={selectedTenent}
													defaultValue={selectedTenent?.displayName}
													options={userTenentList}
													isFullWidth
													updateHandler={handleChange}
													isAutocomplete
													placeholder="Select Tenant"
												/>
											</StackItem>
										</Stack>
									</StackItem>
								</Stack>
							</StackItem>
						</Stack>
					</ModalBody>

					<ModalActions>
						<Button type="text" onClick={hideModal}>
							Cancel
						</Button>
						<Button type="primary" disabled={isTenentListLoading} onClick={handleSaveClick}>
							Save
						</Button>
					</ModalActions>
				</>
			)}
		</Modal>
	);
};

const SettingsDialog: React.FC = () => {
	const [isSettingsDialog, toggleDialog] = useState(false);

	// Initialize dark mode from localStorage on first mount
	useEffect(() => {
		try {
			if (localStorage.getItem('theta-dark-mode') === '1') {
				document.documentElement.dataset.theme = 'dark';
			}
		} catch (e) {
			/* localStorage unavailable */
		}
	}, []);

	return (
		<div className="header-setting">
			<Tooltip title="User Profile Settings">
				<i
					className="icon-settings"
					onClick={() => {
						toggleDialog(true);
					}}
				/>
			</Tooltip>
			{isSettingsDialog && (
				<Sidebar
					hideModal={() => {
						toggleDialog(false);
					}}
				/>
			)}
		</div>
	);
};

export default SettingsDialog;
